import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  ScanLine,
  Camera,
  Play,
  RotateCcw,
  CheckCircle,
  Package,
  Boxes,
  ClipboardList,
  Search,
  XCircle,
  ArrowLeft,
  Plus,
  Trash2,
  MapPin,
  Save,
  ShieldAlert,
} from 'lucide-react';
import { inventoryAPI } from '../services/api';
import { Html5Qrcode } from 'html5-qrcode';
import { toast } from 'sonner';

const readerElementId = 'stockmovel-reader';

function nowLabel(prefix) {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${prefix} ${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function formatDateInput(date) {
  const pad = (value) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function getInitialExpiryLimits() {
  const today = new Date();
  const minimum = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
  const maximum = new Date(today.getFullYear() + 10, today.getMonth(), today.getDate());
  return { min: formatDateInput(minimum), max: formatDateInput(maximum) };
}

export default function StockMovelPage() {
  const expiryLimits = useMemo(getInitialExpiryLimits, []);
  const [loading, setLoading] = useState(true);

  const [mode, setMode] = useState('quick'); // quick | normal | initial
  const [quickCode, setQuickCode] = useState('');
  const [quickProduct, setQuickProduct] = useState(null);
  const [quickBatch, setQuickBatch] = useState(null);
  const [quickSaving, setQuickSaving] = useState(false);

  const [normalSessions, setNormalSessions] = useState([]);
  const [initialSessions, setInitialSessions] = useState([]);

  const [activeSessionId, setActiveSessionId] = useState(null);
  const [activeInitialSessionId, setActiveInitialSessionId] = useState(null);

  const [sessionStatus, setSessionStatus] = useState(null);
  const [initialSessionStatus, setInitialSessionStatus] = useState(null);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCameraModal, setShowCameraModal] = useState(false);

  const [sessionName, setSessionName] = useState('');
  const [initialSessionName, setInitialSessionName] = useState('');
  const [initialSessionNotes, setInitialSessionNotes] = useState('');

  const [manualCode, setManualCode] = useState('');
  const [initialManualCode, setInitialManualCode] = useState('');
  const [manualQuantity, setManualQuantity] = useState('');

  const [lastScan, setLastScan] = useState(null);
  const [cameraError, setCameraError] = useState('');
  const [cameraReady, setCameraReady] = useState(false);

  const [initialResolvedProduct, setInitialResolvedProduct] = useState(null);
  const [initialEntryRows, setInitialEntryRows] = useState([
    { quantity: 1, expiry_date: '', lot_number: '', no_expiry: false },
  ]);
  const [initialLastSave, setInitialLastSave] = useState(null);

  const scannerRef = useRef(null);
  const scannerBusyRef = useRef(false);
  const manualCodeRef = useRef(null);
  const initialManualCodeRef = useRef(null);

  const isInitialMode = mode === 'initial';
  const isQuickMode = mode === 'quick';

  const categories = useMemo(() => {
    const fromInitial = (initialSessionStatus?.entries || []).map((e) => e.category).filter(Boolean);
    const fromResolved = initialResolvedProduct?.category ? [initialResolvedProduct.category] : [];
    return ['Sem categoria', ...Array.from(new Set([...fromInitial, ...fromResolved]))];
  }, [initialResolvedProduct, initialSessionStatus]);

  const loadLists = useCallback(async () => {
    const [normalRes, initialRes] = await Promise.all([
      inventoryAPI.getSessions().catch(() => ({ data: [] })),
      inventoryAPI.getInitialSessions().catch(() => ({ data: [] })),
    ]);

    setNormalSessions(Array.isArray(normalRes?.data) ? normalRes.data : []);
    setInitialSessions(Array.isArray(initialRes?.data) ? initialRes.data : []);
  }, []);

  const loadNormalStatus = useCallback(async (sid) => {
    if (!sid) return;
    const res = await inventoryAPI.getSessionStatus(sid);
    setSessionStatus(res?.data || null);
  }, []);

  const loadInitialStatus = useCallback(async (sid) => {
    if (!sid) return;
    const res = await inventoryAPI.getInitialSessionStatus(sid);
    setInitialSessionStatus(res?.data || null);
  }, []);

  const refreshAll = useCallback(async () => {
    await loadLists();

    if (activeSessionId) {
      await loadNormalStatus(activeSessionId);
    }
    if (activeInitialSessionId) {
      await loadInitialStatus(activeInitialSessionId);
    }
  }, [activeInitialSessionId, activeSessionId, loadInitialStatus, loadLists, loadNormalStatus]);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        await loadLists();
      } catch (_) {
        toast.error('Erro ao carregar inventário móvel');
      } finally {
        setLoading(false);
      }
    })();
  }, [loadLists]);

  useEffect(() => {
    if (activeSessionId) {
      loadNormalStatus(activeSessionId).catch(() => {
        toast.error('Erro ao carregar a sessão de contagem');
      });
    }
  }, [activeSessionId, loadNormalStatus]);

  useEffect(() => {
    if (activeInitialSessionId) {
      loadInitialStatus(activeInitialSessionId).catch(() => {
        toast.error('Erro ao carregar a sessão de stock inicial');
      });
    }
  }, [activeInitialSessionId, loadInitialStatus]);

  const stopScanner = useCallback(async () => {
    scannerBusyRef.current = false;
    setCameraReady(false);

    try {
      if (scannerRef.current) {
        const scanner = scannerRef.current;
        scannerRef.current = null;

        try {
          await scanner.stop();
        } catch (_) {
        }

        try {
          await scanner.clear();
        } catch (_) {
        }
      }
    } catch (_) {
    }
  }, []);

  useEffect(() => {
    if (!showCameraModal) {
      stopScanner();
    }
    return () => {
      stopScanner();
    };
  }, [showCameraModal, stopScanner]);

  const resolveInitialProductByScan = useCallback(async (overrideCode = '') => {
    const sid = activeInitialSessionId;
    if (!sid) {
      toast.error('Abra primeiro uma sessão de stock inicial');
      return;
    }

    const code = String(overrideCode || initialManualCode || '').trim();
    if (!code) {
      toast.error('Leia ou introduza um código');
      return;
    }

    const response = await inventoryAPI.resolveInitialProduct(sid, code);
    const data = response?.data || {};
    const product = data?.product || {};

    const normalizedProduct = {
      product_id: product?.id || data?.product_id || '',
      product_name: product?.name || data?.product_name || '',
      barcode: product?.barcode || data?.barcode || '',
      reference: product?.reference || data?.reference || '',
      category: product?.category || data?.category || 'Sem categoria',
      brand: product?.brand || data?.brand || '',
      cost_price: Number(product?.cost_price ?? data?.cost_price ?? 0),
      sell_price: Number(product?.sell_price ?? data?.sell_price ?? 0),
      existing_rows: Array.isArray(data?.existing_rows) ? data.existing_rows : [],
    };

    setInitialResolvedProduct(normalizedProduct);

    if (normalizedProduct.existing_rows.length > 0) {
      setInitialEntryRows(
        normalizedProduct.existing_rows.map((row) => ({
          quantity: Number(row?.quantity || 1),
          expiry_date: String(row?.expiry_date || ''),
          lot_number: String(row?.lot_number || ''),
          no_expiry: !!row?.no_expiry || !String(row?.expiry_date || '').trim(),
        }))
      );
    } else {
      setInitialEntryRows([{ quantity: 1, expiry_date: '', lot_number: '', no_expiry: false }]);
    }

    setInitialManualCode('');
    toast.success(`Artigo encontrado: ${normalizedProduct.product_name || 'OK'}`);
  }, [activeInitialSessionId, initialManualCode]);

  const handleNormalScanCode = useCallback(async (code) => {
    if (!activeSessionId) {
      toast.error('Abra primeiro uma sessão de contagem');
      return;
    }

    const response = await inventoryAPI.scanItemV2(activeSessionId, code);

    if (response?.data?.success) {
      setLastScan({
        success: true,
        product: response.data.product_name,
        count: response.data.current_count,
        lot: response.data.lot_number,
        expiry: response.data.expiry_date,
      });
      navigator.vibrate?.(50);
      toast.success(`${response.data.product_name} • ${response.data.current_count} un`);
      await loadNormalStatus(activeSessionId);
      return;
    }

    setLastScan({
      success: false,
      message: response?.data?.message || 'Leitura não reconhecida',
    });
    toast.error(response?.data?.message || 'Leitura não reconhecida');
  }, [activeSessionId, loadNormalStatus]);

  const resolveQuickProduct = useCallback(async (overrideCode = '') => {
    const code = String(overrideCode || quickCode || '').trim();
    if (!code) {
      toast.error('Leia ou introduza um código');
      return;
    }
    const response = await inventoryAPI.getMobileProduct(code);
    const data = response?.data || null;
    setQuickProduct(data);
    const matched = (data?.batches || []).find(row => row.matched) || (data?.batches || [])[0] || null;
    setQuickBatch(matched ? { ...matched, expected_quantity: Number(matched.quantity || 0), reason: 'inventory_variance', note: '' } : null);
    setQuickCode('');
    navigator.vibrate?.(50);
    toast.success(data?.product?.name || 'Artigo encontrado');
  }, [quickCode]);

  const handleDecodedCode = useCallback(async (decodedText) => {
    const code = String(decodedText || '').trim();
    if (!code || scannerBusyRef.current) return;

    scannerBusyRef.current = true;

    try {
      await stopScanner();
      setShowCameraModal(false);

      if (isQuickMode) {
        setQuickCode(code);
        await resolveQuickProduct(code);
      } else if (isInitialMode) {
        setInitialManualCode(code);
        await resolveInitialProductByScan(code);
      } else {
        setManualCode(code);
        await handleNormalScanCode(code);
      }
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erro ao processar leitura');
    } finally {
      scannerBusyRef.current = false;
    }
  }, [handleNormalScanCode, isInitialMode, isQuickMode, resolveInitialProductByScan, resolveQuickProduct, stopScanner]);

  const saveQuickBatch = async () => {
    if (!quickBatch?.batch_id) return;
    setQuickSaving(true);
    try {
      await inventoryAPI.adjustMobileBatch(quickBatch.batch_id, {
        quantity: Number(quickBatch.quantity || 0),
        expected_quantity: Number(quickBatch.expected_quantity || 0),
        lot_number: quickBatch.lot_number || '',
        expiry_date: quickBatch.expiry_date || '',
        location_id: quickBatch.location_id || quickProduct?.default_location_id,
        reason: quickBatch.reason || 'inventory_variance',
        note: quickBatch.note || '',
      });
      toast.success('Stock atualizado e movimento auditado');
      const refreshed = await inventoryAPI.getMobileProduct(quickProduct?.product?.barcode || quickProduct?.product?.reference);
      setQuickProduct(refreshed?.data || null);
      setQuickBatch(null);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Não foi possível guardar');
    } finally {
      setQuickSaving(false);
    }
  };

  const applyManualQuantity = useCallback(async () => {
    const code = String(manualCode || '').trim();
    const qty = Number(manualQuantity || 0);

    if (!activeSessionId) {
      toast.error('Abra primeiro uma sessão de contagem');
      return;
    }

    if (!code) {
      toast.error('Introduza ou leia primeiro um código');
      return;
    }

    if (!Number.isFinite(qty) || qty <= 0) {
      toast.error('Introduza uma quantidade válida');
      return;
    }

    for (let i = 0; i < qty; i += 1) {
      await handleNormalScanCode(code);
    }

    setManualQuantity('');
    manualCodeRef.current?.focus();
  }, [activeSessionId, handleNormalScanCode, manualCode, manualQuantity]);


  const startScanner = useCallback(async () => {
    setCameraError('');
    setCameraReady(false);
    setShowCameraModal(true);
  }, []);

  const bootScanner = useCallback(async () => {
    if (!showCameraModal) return;

    setCameraError('');
    setCameraReady(false);

    await stopScanner();

    const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

    let readerEl = null;
    for (let i = 0; i < 30; i += 1) {
      readerEl = document.getElementById(readerElementId);
      if (readerEl) break;
      await wait(120);
    }

    if (!readerEl) {
      setCameraError('Não encontrei a área de leitura da câmara.');
      return;
    }

    try {
      readerEl.innerHTML = '';
    } catch (_) {
    }

    try {
      const probeStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' } },
        audio: false,
      });

      try {
        probeStream.getTracks().forEach((track) => track.stop());
      } catch (_) {
      }
    } catch (probeError) {
      throw new Error(
        `getUserMedia falhou: ${probeError?.message || probeError?.name || 'sem detalhe'}`
      );
    }

    try {
      const scanner = new Html5Qrcode(readerElementId);
      scannerRef.current = scanner;

      const cameras = await Html5Qrcode.getCameras().catch(() => []);
      const preferred = cameras.find((c) =>
        /back|rear|traseira|trás|tras|environment/i.test(String(c?.label || ''))
      );

      const cameraConfig = preferred?.id || { facingMode: { ideal: 'environment' } };

      await scanner.start(
        cameraConfig,
        {
          fps: 10,
          qrbox: { width: 240, height: 240 },
          aspectRatio: 1.333334,
        },
        async (decodedText) => {
          await handleDecodedCode(decodedText);
        },
        () => {}
      );

      setCameraReady(true);
    } catch (error) {
      setCameraReady(false);

      const rawMessage =
        error?.message ||
        error?.name ||
        error?.toString?.() ||
        'Erro desconhecido ao abrir a câmara';

      setCameraError(`Não foi possível abrir a câmara. Detalhe: ${rawMessage}`);
      // ajuda extra no iPhone / Safari
      console.error('StockMovel camera boot error:', error);
    }
  }, [handleDecodedCode, showCameraModal, stopScanner]);

  useEffect(() => {
    if (!showCameraModal) return;

    let cancelled = false;

    const run = async () => {
      await bootScanner();
      if (cancelled) {
        await stopScanner();
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [bootScanner, showCameraModal, stopScanner]);

  const startNormalSession = async () => {
    const name = String(sessionName || '').trim() || nowLabel('Contagem móvel');
    const res = await inventoryAPI.startCounting(name);
    const sid = res?.data?.session_id;
    setActiveSessionId(sid || null);
    setSessionName('');
    setMode('normal');
    setShowCreateModal(false);
    await loadLists();
    if (sid) await loadNormalStatus(sid);
    toast.success('Sessão de contagem iniciada');
  };

  const startInitialSession = async () => {
    const name = String(initialSessionName || '').trim() || nowLabel('Stock inicial móvel');
    const notes = String(initialSessionNotes || '').trim() || null;

    const res = await inventoryAPI.startInitialSession(name, notes);
    const sid = res?.data?.session_id;

    setActiveInitialSessionId(sid || null);
    setInitialSessionName('');
    setInitialSessionNotes('');
    setInitialResolvedProduct(null);
    setInitialEntryRows([{ quantity: 1, expiry_date: '', lot_number: '', no_expiry: false }]);
    setMode('initial');
    setShowCreateModal(false);

    await loadLists();
    if (sid) await loadInitialStatus(sid);

    toast.success('Sessão de stock inicial iniciada');
  };

  const saveInitialResolvedProduct = async (goNext = false) => {
    if (!activeInitialSessionId) {
      toast.error('Sessão inicial não encontrada');
      return;
    }

    const productId = String(initialResolvedProduct?.product_id || '').trim();
    if (!productId) {
      toast.error('Leia primeiro um artigo');
      return;
    }

    const category = String(initialResolvedProduct?.category || '').trim() || 'Sem categoria';
    const brand = String(initialResolvedProduct?.brand || '').trim();
    const costPrice = Number(initialResolvedProduct?.cost_price || 0);
    const sellPrice = Number(initialResolvedProduct?.sell_price || 0);

    const rows = initialEntryRows.map((row) => ({
      quantity: Number(row.quantity || 0),
      expiry_date: row.no_expiry ? '' : String(row.expiry_date || '').trim(),
      lot_number: String(row.lot_number || '').trim(),
      no_expiry: !!row.no_expiry,
      cost_price: costPrice,
      sell_price: sellPrice,
    }));

    const response = await inventoryAPI.saveInitialEntry(activeInitialSessionId, {
      product_id: productId,
      category,
      brand,
      cost_price: costPrice,
      sell_price: sellPrice,
      rows,
    });

    setInitialLastSave(response?.data || null);
    setInitialResolvedProduct(null);
    setInitialManualCode('');
    setInitialEntryRows([{ quantity: 1, expiry_date: '', lot_number: '', no_expiry: false }]);
    await loadInitialStatus(activeInitialSessionId);
    await loadLists();
    toast.success('Artigo gravado');

    if (goNext) {
      setTimeout(async () => {
        try {
          await startScanner();
        } catch (_) {
          initialManualCodeRef.current?.focus();
        }
      }, 180);
      return;
    }

    setTimeout(() => initialManualCodeRef.current?.focus(), 50);
  };

  const undoLastNormalScan = async () => {
    if (!activeSessionId) return;
    const res = await inventoryAPI.undoLastScan(activeSessionId);
    toast.success(`Anulado: ${res?.data?.product || 'última leitura'}`);
    setLastScan(null);
    await loadNormalStatus(activeSessionId);
  };

  const finalizeNormalSession = async () => {
    if (!activeSessionId) return;

    const ok = window.confirm('Finalizar esta sessão geral sem ajustar stock?');
    if (!ok) return;

    await inventoryAPI.finalizeSession(activeSessionId, false, false);

    toast.success('Sessão geral finalizada');
    setActiveSessionId(null);
    setSessionStatus(null);
    setLastScan(null);
    setManualCode('');
    setManualQuantity('');
    await loadLists();
  };

  const finalizeInitialSessionMobile = async () => {
    if (!activeInitialSessionId) return;

    const ok = window.confirm('Finalizar esta sessão de stock inicial e gravar no stock?');
    if (!ok) return;

    await inventoryAPI.finalizeInitialSession(activeInitialSessionId, true);

    toast.success('Sessão de stock inicial finalizada');
    setActiveInitialSessionId(null);
    setInitialSessionStatus(null);
    setInitialResolvedProduct(null);
    setInitialLastSave(null);
    setInitialManualCode('');
    setInitialEntryRows([{ quantity: 1, expiry_date: '', lot_number: '', no_expiry: false }]);
    await loadLists();
  };

  const updateInitialEntryRow = (index, field, value) => {
    setInitialEntryRows((prev) =>
      prev.map((row, idx) => {
        if (idx !== index) return row;
        const next = { ...row, [field]: value };
        if (field === 'no_expiry' && value === true) {
          next.expiry_date = '';
        }
        return next;
      })
    );
  };

  const addInitialEntryRow = () => {
    setInitialEntryRows((prev) => [
      ...prev,
      { quantity: 1, expiry_date: '', lot_number: '', no_expiry: false },
    ]);
  };

  const removeInitialEntryRow = (index) => {
    setInitialEntryRows((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((_, idx) => idx !== index);
    });
  };

  const inProgressNormal = normalSessions.filter((s) => s?.status === 'in_progress');
  const inProgressInitial = initialSessions.filter((s) => s?.status === 'in_progress');

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center p-6">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 rounded-full border-4 border-emerald-500/30 border-t-emerald-400 animate-spin" />
          <p className="mt-4 text-slate-300">A carregar inventário móvel...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white pb-28">
      <style>{`
        #stockmovel-reader {
          position: relative;
        }
        #stockmovel-reader video,
        #stockmovel-reader canvas {
          width: 100% !important;
          height: 100% !important;
          object-fit: cover !important;
          border-radius: 1rem !important;
        }
        #stockmovel-reader > div {
          border: 0 !important;
        }
      `}</style>
        {!((!isInitialMode && activeSessionId) || (isInitialMode && activeInitialSessionId)) && (
          <div className="sticky top-0 z-20 border-b border-slate-800 bg-slate-950/95 backdrop-blur">
            <div className="mx-auto w-full max-w-3xl px-4 py-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <p className="text-xs uppercase tracking-[0.25em] text-emerald-300/80">Inventário e localização</p>
                  <h1 className="text-2xl font-bold">FamaPOS Stock</h1>
                </div>
                <div className="grid grid-cols-3 gap-2 w-full sm:w-auto">
                  <Button
                    type="button"
                    variant={mode === 'quick' ? 'default' : 'outline'}
                    onClick={() => setMode('quick')}
                    className={mode === 'quick' ? 'bg-emerald-500 hover:bg-emerald-600 h-11 rounded-xl px-3' : 'h-11 rounded-xl border-slate-700 px-3'}
                  >
                    Rápido
                  </Button>
                  <Button
                    type="button"
                    variant={mode === 'normal' ? 'default' : 'outline'}
                    onClick={() => setMode('normal')}
                    className={mode === 'normal' ? 'bg-emerald-500 hover:bg-emerald-600 h-11 rounded-xl px-3' : 'h-11 rounded-xl border-slate-700 px-3'}
                  >
                    Contar
                  </Button>
                  <Button
                    type="button"
                    variant={mode === 'initial' ? 'default' : 'outline'}
                    onClick={() => setMode('initial')}
                    className={mode === 'initial' ? 'bg-amber-500 hover:bg-amber-600 text-slate-950 h-11 rounded-xl px-3' : 'h-11 rounded-xl border-slate-700 px-3'}
                  >
                    Inicial
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

      <div className="mx-auto w-full max-w-3xl px-4 py-4 space-y-4">
        {isQuickMode && (
          <>
            <div className="rounded-2xl border border-emerald-500/30 bg-slate-900/80 p-4">
              <h2 className="text-xl font-semibold">Consultar ou corrigir stock</h2>
              <p className="mt-1 text-sm text-slate-400">Leia o código com a câmara traseira. A localização assumida é Loja — Ervanária.</p>
              <Button type="button" onClick={startScanner} className="mt-4 h-16 w-full rounded-2xl bg-emerald-500 text-lg font-bold hover:bg-emerald-600">
                <Camera className="mr-3 h-6 w-6" /> Ler artigo
              </Button>
              <div className="mt-3 flex gap-2">
                <Input ref={manualCodeRef} value={quickCode} onChange={e => setQuickCode(e.target.value)} onKeyDown={e => e.key === 'Enter' && resolveQuickProduct()} placeholder="Código de barras ou referência" className="h-12 border-slate-700 bg-slate-950" />
                <Button type="button" variant="outline" onClick={() => resolveQuickProduct()} className="h-12 rounded-xl border-slate-700"><Search className="h-5 w-5" /></Button>
              </div>
            </div>

            {quickProduct && (
              <div className="rounded-2xl border border-slate-700 bg-slate-900 p-4 space-y-4">
                <div>
                  <p className="text-xs text-emerald-300">{quickProduct.product?.reference || quickProduct.product?.barcode}</p>
                  <h2 className="text-xl font-bold">{quickProduct.product?.name}</h2>
                  <p className="text-sm text-slate-400">Stock total: <strong className="text-white">{quickProduct.product?.total_stock || 0} un</strong></p>
                </div>
                <div className="grid gap-2">
                  {(quickProduct.stock_by_location || []).map(row => (
                    <div key={row.location_id} className="flex items-center justify-between rounded-xl bg-slate-950 px-3 py-3">
                      <span className="flex items-center gap-2 text-sm text-slate-300"><MapPin className="h-4 w-4 text-emerald-400" />{row.location_name}</span>
                      <strong>{row.quantity} un</strong>
                    </div>
                  ))}
                </div>
                <div>
                  <Label className="text-slate-300">Selecionar lote</Label>
                  <select value={quickBatch?.batch_id || ''} onChange={e => { const row = (quickProduct.batches || []).find(item => item.batch_id === e.target.value); setQuickBatch(row ? { ...row, expected_quantity: Number(row.quantity || 0), reason: 'inventory_variance', note: '' } : null); }} className="mt-1 h-12 w-full rounded-xl border border-slate-700 bg-slate-950 px-3 text-white">
                    <option value="">Escolher lote…</option>
                    {(quickProduct.batches || []).map(row => <option key={row.batch_id} value={row.batch_id}>{row.lot_number || 'Sem lote'} · {row.expiry_date || 'Sem validade'} · {row.quantity} un · {row.location_name}</option>)}
                  </select>
                </div>

                {quickBatch && (
                  <div className="space-y-3 rounded-2xl border border-slate-700 bg-slate-950/70 p-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label>Embalagens</Label><Input type="number" min="0" value={quickBatch.quantity} onChange={e => setQuickBatch(row => ({ ...row, quantity: e.target.value }))} className="mt-1 h-12 border-slate-600 bg-white text-lg font-bold text-slate-950" /></div>
                      <div><Label>Validade</Label><Input type="date" value={quickBatch.expiry_date || ''} onChange={e => setQuickBatch(row => ({ ...row, expiry_date: e.target.value }))} className="mt-1 h-12 border-slate-600 bg-white text-slate-950" /></div>
                    </div>
                    <div><Label>Número do lote</Label><Input value={quickBatch.lot_number || ''} onChange={e => setQuickBatch(row => ({ ...row, lot_number: e.target.value }))} className="mt-1 h-12 border-slate-600 bg-white text-slate-950" /></div>
                    <div><Label>Localização</Label><select value={quickBatch.location_id || quickProduct.default_location_id} onChange={e => setQuickBatch(row => ({ ...row, location_id: e.target.value }))} className="mt-1 h-12 w-full rounded-xl border border-slate-600 bg-white px-3 text-slate-950">{(quickProduct.locations || []).map(location => <option key={location.id} value={location.id}>{location.name}</option>)}</select></div>
                    {Number(quickBatch.quantity) !== Number(quickBatch.expected_quantity) && <div className="rounded-xl border border-amber-600/50 bg-amber-950/30 p-3 text-sm text-amber-200 flex gap-2"><ShieldAlert className="h-5 w-5 shrink-0" /><span>Diferença de {Number(quickBatch.quantity) - Number(quickBatch.expected_quantity)} unidade(s). Ficará registada como variação de inventário não justificada até análise.</span></div>}
                    <div><Label>Observação {Number(quickBatch.quantity) !== Number(quickBatch.expected_quantity) ? '(obrigatória)' : '(opcional)'}</Label><Input value={quickBatch.note || ''} onChange={e => setQuickBatch(row => ({ ...row, note: e.target.value }))} placeholder="Ex.: recontado duas vezes; embalagem não encontrada" className="mt-1 h-12 border-slate-600 bg-white text-slate-950" /></div>
                    <Button disabled={quickSaving} onClick={saveQuickBatch} className="h-14 w-full rounded-2xl bg-emerald-500 text-base font-bold hover:bg-emerald-600"><Save className="mr-2 h-5 w-5" />{quickSaving ? 'A guardar…' : 'Guardar correção auditada'}</Button>
                  </div>
                )}
              </div>
            )}
          </>
        )}
        {mode === 'normal' && !activeSessionId && (
          <>
            <div className="rounded-2xl border border-slate-800 bg-slate-900/70 p-4">
              <h2 className="text-xl font-semibold">Contagem geral</h2>
              <p className="mt-1 text-sm text-slate-400">Crie uma sessão nova ou retome uma sessão em andamento.</p>

              <div className="mt-4 grid gap-3">
                <Button
                  type="button"
                  onClick={() => {
                    setSessionName(nowLabel('Contagem móvel'));
                    setShowCreateModal(true);
                  }}
                  className="h-14 rounded-2xl bg-emerald-500 text-base font-semibold hover:bg-emerald-600"
                >
                  <Play className="mr-2 h-5 w-5" />
                  Nova contagem geral
                </Button>

                {inProgressNormal.map((s) => (
                  <Button
                    key={s.session_id || s.id || s._id || s.name}
                    type="button"
                    variant="outline"
                    onClick={async () => {
                      const sid = s.session_id || s.id || s._id;
                      setActiveSessionId(sid);
                      setMode('normal');
                      await loadNormalStatus(sid);
                    }}
                    className="h-14 rounded-2xl border-slate-700 justify-between"
                  >
                    <span className="truncate">Retomar: {s.name || 'sessão em andamento'}</span>
                    <RotateCcw className="h-5 w-5" />
                  </Button>
                ))}
              </div>
            </div>
          </>
        )}

        {isInitialMode && !activeInitialSessionId && (
          <>
            <div className="rounded-2xl border border-slate-800 bg-slate-900/70 p-4">
              <h2 className="text-xl font-semibold">Stock inicial</h2>
              <p className="mt-1 text-sm text-slate-400">Registe artigo a artigo com quantidade, validade e lote.</p>

              <div className="mt-4 grid gap-3">
                <Button
                  type="button"
                  onClick={() => {
                    setInitialSessionName(nowLabel('Stock inicial móvel'));
                    setShowCreateModal(true);
                  }}
                  className="h-14 rounded-2xl bg-amber-500 text-base font-semibold text-slate-950 hover:bg-amber-600"
                >
                  <ClipboardList className="mr-2 h-5 w-5" />
                  Novo stock inicial
                </Button>

                {inProgressInitial.map((s) => (
                  <Button
                    key={s.session_id || s.id || s._id || s.name}
                    type="button"
                    variant="outline"
                    onClick={async () => {
                      const sid = s.session_id || s.id || s._id;
                      setActiveInitialSessionId(sid);
                      setMode('initial');
                      await loadInitialStatus(sid);
                    }}
                    className="h-14 rounded-2xl border-slate-700 justify-between"
                  >
                    <span className="truncate">Retomar: {s.name || 'sessão em andamento'}</span>
                    <RotateCcw className="h-5 w-5" />
                  </Button>
                ))}
              </div>
            </div>
            </>
          )}
          {mode === 'normal' && activeSessionId && (
            <>
              <div className="sticky top-0 z-10 -mx-4 border-b border-slate-800 bg-slate-950/95 px-4 pb-2 backdrop-blur">
                <div className="space-y-1.5">
                  <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/10 p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="text-sm text-emerald-300">Sessão ativa</p>
                        <h2 className="text-xl font-semibold">{sessionStatus?.name || 'Contagem em andamento'}</h2>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setActiveSessionId(null);
                          setSessionStatus(null);
                          setLastScan(null);
                          setManualCode('');
                          setManualQuantity('');
                        }}
                        className="h-9 rounded-xl border-slate-700 px-3.5"
                      >
                        <ArrowLeft className="mr-2 h-4 w-4" />
                        Sair
                      </Button>
                    </div>

                    <div className="mt-3 grid grid-cols-2 gap-2">
                      <div className="rounded-xl bg-slate-950/50 p-2.5">
                        <p className="text-[10px] uppercase tracking-wide text-slate-400">Produtos</p>
                        <p className="mt-1 text-xl font-bold">{sessionStatus?.products_counted || 0}</p>
                      </div>
                      <div className="rounded-lg bg-slate-950/50 p-2">
                        <p className="text-[10px] uppercase tracking-wide text-slate-400">Unidades</p>
                        <p className="mt-1 text-2xl font-bold text-emerald-300">{sessionStatus?.total_items_counted || 0}</p>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-slate-800 bg-slate-900/70 p-3 space-y-3">
                    <h3 className="text-lg font-semibold">Ler artigo</h3>

                    <Button
                      type="button"
                      onClick={startScanner}
                      className="h-14 w-full rounded-2xl bg-emerald-500 text-base font-semibold hover:bg-emerald-600"
                    >
                      <Camera className="mr-2 h-4 w-4" />
                      Abrir câmara
                    </Button>

                    <div className="space-y-1.5">
                      <Label className="text-slate-300">Código manual</Label>
                      <Input
                        ref={manualCodeRef}
                        value={manualCode}
                        onChange={(e) => setManualCode(e.target.value)}
                        onKeyDown={async (e) => {
                          if (e.key !== 'Enter') return;
                          e.preventDefault();
                          await handleNormalScanCode(String(manualCode || '').trim());
                          setManualCode('');
                        }}
                        placeholder="Leia ou escreva o código"
                        className="h-11 rounded-2xl border-slate-700 bg-slate-950 text-sm"
                      />
                    </div>

                    <div className="grid grid-cols-[44px_1fr_44px_96px] gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setManualQuantity(String(Math.max(0, Number(manualQuantity || 0) - 1)))}
                        className="h-12 rounded-xl border-slate-700 px-0 text-lg"
                      >
                        -
                      </Button>
                      <Input
                        value={manualQuantity}
                        onChange={(e) => setManualQuantity(e.target.value.replace(/[^0-9]/g, ''))}
                        placeholder="Qtd"
                        inputMode="numeric"
                        className="h-12 rounded-xl border-slate-700 bg-slate-950 text-center text-base font-semibold"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setManualQuantity(String((Number(manualQuantity || 0) || 0) + 1))}
                        className="h-12 rounded-xl border-slate-700 px-0 text-lg"
                      >
                        +
                      </Button>
                      <Button
                        type="button"
                        onClick={applyManualQuantity}
                        disabled={!String(manualCode || '').trim() || !String(manualQuantity || '').trim() || Number(manualQuantity || 0) <= 0}
                        className="h-12 rounded-xl bg-emerald-500 text-sm font-semibold hover:bg-emerald-600"
                      >
                        Aplicar
                      </Button>
                    </div>

                    <div className="grid grid-cols-3 gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={undoLastNormalScan}
                        disabled={!sessionStatus?.scan_history?.length}
                        className="h-12 rounded-xl border-amber-500/40 text-sm font-semibold text-amber-300"
                      >
                        <RotateCcw className="mr-1 h-4 w-4" />
                        Anular
                      </Button>

                      <Button
                        type="button"
                        variant="outline"
                        onClick={async () => {
                          await refreshAll();
                        }}
                        className="h-12 rounded-xl border-slate-700 text-sm font-semibold"
                      >
                        Atualizar
                      </Button>

                      <Button
                        type="button"
                        variant="outline"
                        onClick={finalizeNormalSession}
                        className="h-12 rounded-xl border-emerald-500/40 text-sm font-semibold text-emerald-300"
                      >
                        Finalizar
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              {lastScan && (
                <div className={`rounded-2xl border p-3 ${lastScan.success ? 'border-emerald-500/30 bg-emerald-500/10' : 'border-rose-500/30 bg-rose-500/10'}`}>
                  {lastScan.success ? (
                    <div className="flex items-start gap-3">
                      <CheckCircle className="mt-0.5 h-5 w-5 text-emerald-300" />
                      <div>
                        <p className="text-base font-semibold">{lastScan.product}</p>
                        <p className="mt-1 text-sm text-emerald-200">
                          Contagem atual: {lastScan.count} un
                          {lastScan.lot ? ` • Lote: ${lastScan.lot}` : ''}
                          {lastScan.expiry ? ` • Validade: ${lastScan.expiry}` : ''}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start gap-3">
                      <XCircle className="mt-0.5 h-5 w-5 text-rose-300" />
                      <p className="text-sm">{lastScan.message}</p>
                    </div>
                  )}
                </div>
              )}

              <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-3">
                <h3 className="text-base font-semibold">Artigos lidos nesta sessão</h3>

                {sessionStatus?.scan_history?.length ? (
                  <div className="mt-3 space-y-2">
                    {[...sessionStatus.scan_history].slice().reverse().map((row, index) => (
                      <div
                        key={`${row?.product_name || 'item'}-${row?.barcode || index}-${index}`}
                        className="rounded-xl border border-slate-800 bg-slate-950/40 p-3"
                      >
                        <p className="text-sm font-semibold text-white">{row?.product_name || "Artigo"}</p>
                        <p className="mt-1 text-xs text-slate-300">
                          Qtd: {row?.current_count ?? row?.quantity ?? 1}
                          {row?.lot_number ? ` • Lote: ${row.lot_number}` : ''}
                          {row?.expiry_date ? ` • Validade: ${row.expiry_date}` : ''}
                        </p>
                        {row?.barcode ? <p className="mt-1 text-[11px] text-slate-500">{row.barcode}</p> : null}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="mt-3 text-sm text-slate-400">Ainda sem leituras nesta sessão.</p>
                )}
              </div>
            </>
          )}



            {isInitialMode && activeInitialSessionId && (
              <>
                <div className="sticky top-0 z-10 -mx-4 border-b border-slate-800 bg-slate-950/95 px-3 pb-2 backdrop-blur">
                  <div className="space-y-2">
                    <div className="rounded-xl border border-amber-500/20 bg-amber-500/10 p-2.5">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-xs text-amber-200">Sessão ativa</p>
                          <h2 className="truncate text-lg font-semibold">{initialSessionStatus?.name || 'Stock inicial em andamento'}</h2>
                        </div>

                        <div className="flex gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                              setActiveInitialSessionId(null);
                              setInitialSessionStatus(null);
                              setInitialResolvedProduct(null);
                              setInitialManualCode('');
                            }}
                            className="h-9 rounded-lg border-slate-700 px-3 text-xs"
                          >
                            <ArrowLeft className="mr-1 h-4 w-4" />
                            Sair
                          </Button>

                          <Button
                            type="button"
                            variant="outline"
                            onClick={finalizeInitialSessionMobile}
                            className="h-9 rounded-lg border-amber-500/40 px-3 text-xs text-amber-200"
                          >
                            Finalizar
                          </Button>
                        </div>
                      </div>

                      <div className="mt-2 grid grid-cols-3 gap-1.5">
                        <div className="rounded-lg bg-slate-950/50 p-2">
                          <p className="text-[10px] uppercase tracking-wide text-slate-400">Artigos</p>
                          <p className="mt-1 text-xl font-bold">{initialSessionStatus?.products_counted || 0}</p>
                        </div>
                        <div className="rounded-lg bg-slate-950/50 p-2">
                          <p className="text-[10px] uppercase tracking-wide text-slate-400">Linhas</p>
                          <p className="mt-1 text-xl font-bold">{initialSessionStatus?.rows_counted || 0}</p>
                        </div>
                        <div className="rounded-lg bg-slate-950/50 p-2">
                          <p className="text-[10px] uppercase tracking-wide text-slate-400">Unidades</p>
                          <p className="mt-1 text-xl font-bold text-amber-200">{initialSessionStatus?.total_units || 0}</p>
                        </div>
                      </div>
                    </div>

                    <div className="rounded-xl border border-slate-800 bg-slate-900/70 p-2.5 space-y-2">
                      <div className="flex items-center justify-between gap-2">
                        <h3 className="text-lg font-semibold">Ler artigo</h3>
                        <Button
                          type="button"
                          onClick={startScanner}
                          className="h-9 rounded-lg bg-amber-500 px-3 text-xs font-semibold text-slate-950 hover:bg-amber-600"
                        >
                          <Camera className="mr-1 h-4 w-4" />
                          Câmara
                        </Button>
                      </div>

                      <div className="grid grid-cols-[1fr_110px] gap-2">
                        <Input
                          ref={initialManualCodeRef}
                          value={initialManualCode}
                          onChange={(e) => setInitialManualCode(e.target.value)}
                          onKeyDown={async (e) => {
                            if (e.key !== 'Enter') return;
                            e.preventDefault();
                            await resolveInitialProductByScan();
                          }}
                          placeholder="Leia ou escreva o código"
                          className="h-9 rounded-xl border-slate-700 bg-slate-950 text-sm"
                        />

                        <Button
                          type="button"
                          onClick={() => resolveInitialProductByScan()}
                          className="h-9 rounded-xl bg-amber-500 text-xs font-semibold text-slate-950 hover:bg-amber-600"
                        >
                          <Search className="mr-1 h-4 w-4" />
                          Resolver
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>

                {initialResolvedProduct ? (
                  <>
                    <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/10 p-2.5 space-y-2">
                      <div>
                        <p className="line-clamp-2 text-base font-semibold">{initialResolvedProduct.product_name}</p>
                        <p className="mt-0.5 text-xs text-slate-300">
                          {initialResolvedProduct.category || 'Sem categoria'}
                          {initialResolvedProduct.barcode ? ` • ${initialResolvedProduct.barcode}` : ''}
                        </p>
                      </div>

                      <div className="grid gap-2">
                        <div>
                          <Label className="text-xs">Categoria</Label>
                          <select
                            value={initialResolvedProduct.category || 'Sem categoria'}
                            onChange={(e) =>
                              setInitialResolvedProduct((prev) => ({
                                ...prev,
                                category: e.target.value,
                              }))
                            }
                            className="mt-1 h-9 w-full rounded-xl border border-slate-700 bg-slate-950 px-3 text-sm text-white outline-none"
                          >
                            {categories.map((cat) => (
                              <option key={cat} value={cat}>{cat}</option>
                            ))}
                          </select>
                        </div>

                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-xs">Custo</Label>
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              value={initialResolvedProduct.cost_price ?? 0}
                              onChange={(e) =>
                                setInitialResolvedProduct((prev) => ({
                                  ...prev,
                                  cost_price: e.target.value,
                                }))
                              }
                              className="mt-1 h-9 rounded-xl border-slate-700 bg-slate-950 text-sm"
                            />
                          </div>

                          <div>
                            <Label className="text-xs">PVP</Label>
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              value={initialResolvedProduct.sell_price ?? 0}
                              onChange={(e) =>
                                setInitialResolvedProduct((prev) => ({
                                  ...prev,
                                  sell_price: e.target.value,
                                }))
                              }
                              className="mt-1 h-9 rounded-xl border-slate-700 bg-slate-950 text-sm"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="space-y-2">
                        {initialEntryRows.map((row, index) => (
                          <div key={index} className="rounded-xl border border-slate-700 bg-slate-950/60 p-2.5 space-y-2">
                            <div className="flex items-center justify-between">
                              <p className="text-sm font-semibold">Linha {index + 1}</p>
                              <Button
                                type="button"
                                variant="ghost"
                                onClick={() => removeInitialEntryRow(index)}
                                disabled={initialEntryRows.length <= 1}
                                className="h-8 rounded-lg px-2 text-rose-300"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>

                            <div>
                              <Label className="text-xs">Quantidade</Label>
                              <div className="mt-1 grid grid-cols-[36px_1fr_36px] gap-1.5">
                                <Button
                                  type="button"
                                  variant="outline"
                                  onClick={() => updateInitialEntryRow(index, 'quantity', String(Math.max(1, Number(row.quantity || 1) - 1)))}
                                  className="h-9 rounded-lg border-slate-700 px-0 text-base"
                                >
                                  -
                                </Button>
                                <Input
                                  type="number"
                                  min="1"
                                  step="1"
                                  inputMode="numeric"
                                  value={row.quantity}
                                  onChange={(e) => updateInitialEntryRow(index, 'quantity', e.target.value)}
                                  className="h-9 rounded-lg border-slate-700 bg-slate-900 text-center text-sm font-semibold"
                                />
                                <Button
                                  type="button"
                                  variant="outline"
                                  onClick={() => updateInitialEntryRow(index, 'quantity', String((Number(row.quantity || 0) || 0) + 1))}
                                  className="h-9 rounded-lg border-slate-700 px-0 text-base"
                                >
                                  +
                                </Button>
                              </div>
                            </div>

                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <Label className="text-xs">Validade</Label>
                                <Input
                                  type="date"
                                  min={expiryLimits.min}
                                  max={expiryLimits.max}
                                  value={row.expiry_date}
                                  onChange={(e) => updateInitialEntryRow(index, 'expiry_date', e.target.value)}
                                  disabled={row.no_expiry}
                                  title={`Escolha uma data entre ${expiryLimits.min} e ${expiryLimits.max}`}
                                  className="mt-1 h-9 rounded-xl border-slate-700 bg-slate-900 text-xs"
                                />
                              </div>

                              <div>
                                <Label className="text-xs">Lote</Label>
                                <Input
                                  value={row.lot_number}
                                  onChange={(e) => updateInitialEntryRow(index, 'lot_number', e.target.value)}
                                  placeholder="Automático"
                                  className="mt-1 h-9 rounded-xl border-slate-700 bg-slate-900 text-xs"
                                />
                              </div>
                            </div>

                            <label className="flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-900/70 px-3 py-2">
                              <input
                                type="checkbox"
                                checked={!!row.no_expiry}
                                onChange={(e) => updateInitialEntryRow(index, 'no_expiry', e.target.checked)}
                                className="h-4 w-4"
                              />
                              <span className="text-xs">Artigo sem validade</span>
                            </label>
                          </div>
                        ))}
                      </div>

                      <Button
                        type="button"
                        variant="outline"
                        onClick={addInitialEntryRow}
                        className="h-9 w-full rounded-xl border-slate-700 text-xs font-semibold"
                      >
                        <Plus className="mr-1 h-4 w-4" />
                        Adicionar validade
                      </Button>
                    </div>

                    <div className="h-20" />

                    <div className="fixed bottom-0 left-0 right-0 z-20 border-t border-slate-800 bg-slate-950/95 px-3 py-2 backdrop-blur">
                      <div className="mx-auto grid w-full max-w-3xl gap-1.5">
                        <Button
                          type="button"
                          onClick={() => saveInitialResolvedProduct(false)}
                          className="h-10 w-full rounded-xl bg-emerald-500 text-sm font-semibold hover:bg-emerald-600"
                        >
                          <CheckCircle className="mr-2 h-4 w-4" />
                          Gravar artigo
                        </Button>

                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => saveInitialResolvedProduct(true)}
                          className="h-10 w-full rounded-xl border-emerald-500/40 text-sm font-semibold text-emerald-300"
                        >
                          <ScanLine className="mr-2 h-4 w-4" />
                          Gravar e ler próximo
                        </Button>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="rounded-xl border border-dashed border-slate-700 bg-slate-900/40 p-4 text-center">
                    <Package className="mx-auto h-7 w-7 text-slate-500" />
                    <p className="mt-2 text-sm font-semibold">À espera de artigo</p>
                    <p className="mt-1 text-xs text-slate-400">Leia um código para abrir o formulário.</p>
                  </div>
                )}

                {initialLastSave && (
                  <div className="rounded-xl border border-slate-800 bg-slate-900/70 p-2.5">
                    <p className="text-xs text-slate-400">Último registo</p>
                    <p className="mt-1 text-sm font-semibold">{initialLastSave.product_name || 'Artigo gravado'}</p>
                    <p className="mt-1 text-xs text-emerald-300">
                      {initialLastSave.rows_saved || 0} linha(s) • {initialLastSave.total_quantity || 0} unidade(s)
                    </p>
                  </div>
                )}
              </>
            )}
        <Dialog
          open={showCreateModal}
          onOpenChange={setShowCreateModal}
        >
        <DialogContent className="w-[calc(100vw-24px)] max-w-[calc(100vw-24px)] rounded-2xl border-slate-800 bg-slate-950 p-4">
          {isQuickMode ? (
            <DialogDescription className="text-slate-300">
              Aponte a câmara traseira ao código de barras do artigo.
            </DialogDescription>
          ) : !isInitialMode ? (
            <>
              <DialogHeader>
                <DialogTitle className="text-white text-2xl">Nova contagem geral</DialogTitle>
                <DialogDescription className="text-slate-400">
                  Dê um nome à sessão.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-1.5">
                <div>
                  <Label>Nome</Label>
                  <Input
                    value={sessionName}
                    onChange={(e) => setSessionName(e.target.value)}
                    className="mt-2 h-14 rounded-2xl border-slate-700 bg-slate-900 text-lg"
                  />
                </div>

                <Button
                  type="button"
                  onClick={startNormalSession}
                  className="h-14 w-full rounded-2xl bg-emerald-500 text-base font-semibold hover:bg-emerald-600"
                >
                  <Play className="mr-2 h-5 w-5" />
                  Iniciar
                </Button>
              </div>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="text-white text-2xl">Novo stock inicial</DialogTitle>
                <DialogDescription className="text-slate-400">
                  Crie a sessão para começar o registo móvel.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-1.5">
                <div>
                  <Label>Nome</Label>
                  <Input
                    value={initialSessionName}
                    onChange={(e) => setInitialSessionName(e.target.value)}
                    className="mt-2 h-14 rounded-2xl border-slate-700 bg-slate-900 text-lg"
                  />
                </div>

                <div>
                  <Label>Notas</Label>
                  <Input
                    value={initialSessionNotes}
                    onChange={(e) => setInitialSessionNotes(e.target.value)}
                    className="mt-2 h-14 rounded-2xl border-slate-700 bg-slate-900 text-lg"
                    placeholder="Ex: prateleiras A-F"
                  />
                </div>

                <Button
                  type="button"
                  onClick={startInitialSession}
                  className="h-10 w-full rounded-xl bg-amber-500 text-sm font-semibold text-slate-950 hover:bg-amber-600"
                >
                  <ClipboardList className="mr-2 h-5 w-5" />
                  Iniciar
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={showCameraModal}
        onOpenChange={(open) => {
          setShowCameraModal(open);
          if (!open) {
            stopScanner();
            setCameraError('');
          }
        }}
      >
        <DialogContent className="w-[calc(100vw-20px)] max-w-[calc(100vw-20px)] rounded-2xl border-slate-800 bg-slate-950 p-4">
          <DialogHeader>
            <DialogTitle className="text-white text-2xl">Leitura por câmara</DialogTitle>
            <DialogDescription className="text-slate-400 text-base">
              A leitura é automática. Aponte para o código e aguarde.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div
              id={readerElementId}
              className="min-h-[340px] w-full overflow-hidden rounded-2xl border border-slate-800 bg-black"
            />

            {cameraError ? (
              <div className="rounded-2xl border border-rose-500/30 bg-rose-500/10 p-4 text-sm text-rose-300">
                {cameraError}
              </div>
            ) : (
              <div className="rounded-2xl border border-slate-800 bg-slate-900/70 p-4 text-sm text-slate-300">
                {cameraReady ? 'Scanner ativo. Não precisa de tocar em mais nada.' : 'A preparar a câmara...'}
              </div>
            )}

            {cameraError && (
              <Button
                type="button"
                onClick={bootScanner}
                className="h-10 w-full rounded-xl bg-amber-500 text-sm font-semibold text-slate-950 hover:bg-amber-600"
              >
                Tentar novamente
              </Button>
            )}

            <Button
              type="button"
              variant="outline"
              onClick={() => {
                stopScanner();
                setShowCameraModal(false);
              }}
              className="h-14 w-full rounded-2xl border-slate-700 text-base font-semibold"
            >
              Fechar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
    </div>
  );
}
